import { Box } from "@rebass/grid";
import * as applicationActions from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.actions";
import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Modal, TextField } from "@rentiohq/web-shared/dist/components";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { getFlagTooltip } from "./FlagLabel";

// Matches the api-next limit on the reason.
const REASON_MAX_LENGTH = 2000;

/**
 * Flags one candidate for the office, or removes the flag. The
 * reason is required: a flag without one tells a colleague nothing.
 * Nothing else happens to the candidate: no rejection, no mail.
 */
export const FlagModal = (props: {
  application: IApplication;
  candidateName: string;
  onClose: () => void;
}) => {
  const { application, candidateName, onClose } = props;
  const dispatch = useDispatch();

  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isRemove = !!application.flag;
  const trimmedReason = reason.trim();

  const handleSubmit = () => {
    if (!isRemove && !trimmedReason) return;

    setIsSubmitting(true);
    dispatch(
      applicationActions.setApplicationFlagStart.getAction({
        id: application.id,
        reason: isRemove ? null : trimmedReason,
        onSuccess: () => onClose(),
        onFailure: () => setIsSubmitting(false),
      }),
    );
  };

  return (
    <Modal
      width="small"
      onClose={onClose}
      heading={getLocalizedText(
        isRemove
          ? "application.flag.remove.heading"
          : "application.flag.add.heading",
      )}
      actions={[
        {
          content: getLocalizedText("system.cancel"),
          appearance: "outline",
          onClick: onClose,
        },
        {
          content: getLocalizedText(
            isRemove
              ? "application.flag.remove.action"
              : "application.flag.add.action",
          ),
          appearance: "primary",
          onClick: handleSubmit,
          isDisabled: isSubmitting || (!isRemove && !trimmedReason),
        },
      ]}
    >
      <Box p={3} onClick={(event: React.MouseEvent) => event.stopPropagation()}>
        {isRemove ? (
          <>
            <Box mb={2}>
              {getLocalizedText("application.flag.remove.body", {
                name: candidateName,
              })}
            </Box>
            <Box style={{ whiteSpace: "pre-line" }}>
              {getFlagTooltip(application.flag!)}
            </Box>
          </>
        ) : (
          <>
            <Box mb={2}>
              {getLocalizedText("application.flag.add.body", {
                name: candidateName,
              })}
            </Box>
            <TextField
              name="flagReason"
              label={getLocalizedText("application.flag.reason")}
              value={reason}
              multiline={true}
              maxLength={REASON_MAX_LENGTH}
              onChange={(
                event: React.ChangeEvent<
                  HTMLInputElement & HTMLTextAreaElement
                >,
              ) => setReason(event.target.value)}
              autoFocus
            />
          </>
        )}
      </Box>
    </Modal>
  );
};
